import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FiShare2 } from "react-icons/fi";
import api from "src/services/api";
import { useMediaQuery } from "@mui/material";
import NavBar from "../../components/organisms/navbar";
import { tournamentsData } from "src/data/tournamentsData";
import TournamentProgress from "src/components/organisms/tournamentProgress";
import BannerDialog from "./BannerDialog";
import { TfBtn, TfBadge, TfChip } from "src/styles/ds-components";
import {
  HeroSection,
  HeroContent,
  HeroEyebrow,
  HeroTitle,
  HeroSubtitle,
  StatsRow,
  StatItem,
  StatNumber,
  StatLabel,
  ControlsSection,
  SearchBar,
  SearchInput,
  FilterRow,
  FilterLabel,
  ViewControls,
  ResultsCount,
  TournamentGridContainer,
  TournamentGrid,
  TournamentCard,
  CardBanner,
  CardBannerOverlay,
  CardBadges,
  CardBody,
  CardTitle,
  CardBottom,
  CardFooter,
  ShareBubble,
  PriceSection,
  PriceTier,
  PriceTierName,
  PriceTierDot,
  PriceTierRight,
  PriceTierValue,
  PriceTierDeadline,
  CardWarning,
  CapacitySection,
  DetailOverlay,
  DetailPanel,
  DetailHero,
  DetailHeroOverlay,
  DetailBack,
  DetailBadges,
  DetailBody,
  DetailTitle,
  DetailSection,
  DetailSectionTitle,
  DetailDescription,
  DetailCapacity,
  DetailActions,
  AdCard,
  AdClose,
  AdBody,
  AdProgressBar,
  LoadingIndicator,
  EmptyState,
  CardTopBar,
  CardTopBarActions,
  OssBubble,
  OssLabel,
  OssCount,
  OssParticle,
  OssModalOverlay,
  OssModalBox,
  OssModalHeader,
  OssModalList,
  OssModalItem,
  OssModalAvatar,
  OssModalInfo,
  OssComboBar,
  OssComboFloat,
  OssExplosion,
} from "./styles";

type FilterType = "all" | "open" | "closed" | "free" | "limited";

const ITEMS_PER_BATCH = 8;

const FREE_REGEX = /INSCRI[ÇC][ÃA]O\s+GRATUITA/i;

const formatCount = (n: number): string => {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}Bi`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}Mi`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
};

const getBadgeStatus = (t: any): "open" | "closed" | "free" | "filling" => {
  if (t.isClosed) return "closed";
  if (FREE_REGEX.test(t.description)) return "free";
  if (t.amountRegistrationPaid) return "filling";
  return "open";
};

const getBadgeLabel = (status: string) => {
  switch (status) {
    case "closed":  return "Encerrado";
    case "free":    return "Gratuito";
    case "filling": return "Vagas Limitadas";
    default:        return "Inscrições Abertas";
  }
};

const FILTERS: { key: FilterType; label: string }[] = [
  { key: "all",     label: "Todos" },
  { key: "open",    label: "✅ Abertas" },
  { key: "closed",  label: "🔒 Encerrados" },
  { key: "free",    label: "🆓 Gratuitos" },
  { key: "limited", label: "🔥 Vagas Limitadas" },
];

/** Extrai lotes do HTML description com todas as linhas de preço */
type PriceLine = { price: string; category: string };
type Tier = {
  name: string;
  prices: PriceLine[];
  deadline: string;
  isFirst: boolean;
};

const extractPriceTiers = (description: string): Tier[] => {
  const tiers: Tier[] = [];
  // Remove conteúdo dentro de comentários HTML antes de processar
  const cleanDescription = description.replace(/<!--[\s\S]*?-->/g, '');
  const loteRegex = /LOTE\s+(\d+|ÚNICO)\s*[-–—]?\s*\(?([^)<]*)\)?/gi;
  // Captura "R$ XX,XX" + o texto que vem depois (categoria)
  const priceLineRegex = /(R\$\s*[\d.,]+)\s*([^\n<]*)/g;
  const deadlineRegex = /(?:Pagamento\s+)?(?:até|Até)\s+(\d{2}\/\w{3}\/\d{4})/gi;

  const loteMatches = [...description.matchAll(loteRegex)];
  const priceMatches = [...description.matchAll(priceLineRegex)];
  const deadlineMatches = [...description.matchAll(deadlineRegex)];

  if (loteMatches.length > 0) {
    loteMatches.forEach((match, i) => {
      const loteNum = match[1];
      const loteType = match[2]?.trim() || "";
      const name = `Lote ${loteNum}${loteType ? ` — ${loteType}` : ""}`;

      const afterPos = match.index || 0;
      const nextLotePos = loteMatches[i + 1]?.index || cleanDescription.length;

      // Pega TODAS as linhas de preço dentro deste lote
      const relevantPrices = priceMatches.filter(
        p => (p.index || 0) > afterPos && (p.index || 0) < nextLotePos
      );
      const prices: PriceLine[] = relevantPrices.map(p => ({
        price: p[1].trim(),
        category: p[2]?.trim() || "",
      }));

      const relevantDeadlines = deadlineMatches.filter(
        d => (d.index || 0) > afterPos && (d.index || 0) < nextLotePos
      );
      const deadline = relevantDeadlines[0]?.[1] ? `Até ${relevantDeadlines[0][1]}` : "";

      tiers.push({ name, prices, deadline, isFirst: i === 0 });
    });
  } else if (FREE_REGEX.test(cleanDescription)) {
    tiers.push({
      name: "Inscrição Gratuita",
      prices: [{ price: "GRÁTIS", category: "" }],
      deadline: "",
      isFirst: true,
    });
  }

  return tiers;
};

const buildShareText = (t: any): string => {
  const url = `${window.location.origin}/tournament/${t.link}`;
  const tiers = extractPriceTiers(t.description);
  let text = `🥋 ${t.title.toUpperCase()}\n`;

  if (tiers.length > 0) {
    text += "\n";
    tiers.forEach((tier) => {
      text += `📌 ${tier.name}`;
      if (tier.deadline) text += ` (${tier.deadline})`;
      text += "\n";
      tier.prices.forEach((p) => {
        text += `   ${p.price}`;
        if (p.category) text += ` — ${p.category}`;
        text += "\n";
      });
    });
  }

  text += `\nSaiba mais: ${url}`;
  return text;
};

type OssParticleData = {
  id: number;
  x: number;
  y: number;
  delay: number;
  color: "red" | "blue";
};

const Home = () => {
  const AD_LIST = ["intheguard", "tekton", "redlions", "akto"] as const;
  type AdType = typeof AD_LIST[number];
  const [adOrder] = useState<AdType[]>(() => {
    const arr = [...AD_LIST];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  });
  const [adIndex, setAdIndex] = useState(0);
  const currentAd = adOrder[adIndex];
  const [showAd, setShowAd] = useState(true);
  const [showBanner, setShowBanner] = useState(true);
  const [displayCount, setDisplayCount] = useState(ITEMS_PER_BATCH);
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTournament, setSelectedTournament] = useState<any | null>(null);

  /* ── OSS state ── */
  const navigate = useNavigate();
  const [ossMap, setOssMap] = useState<Record<string | number, number>>({});
  const [ossParticles, setOssParticles] = useState<OssParticleData[]>([]);
  const [ossCombo, setOssCombo] = useState<Record<string | number, number>>({});
  const [ossComboResult, setOssComboResult] = useState<Record<string | number, { count: number; x: number; y: number }>>({});
  const [ossExplosion, setOssExplosion] = useState<Record<string | number, boolean>>({});
  const [allowAnonymousOss, setAllowAnonymousOss] = useState(false);
  const allowAnonymousOssRef = useRef(false);
  const ossParticleIdRef = useRef(0);
  const ossConsecutiveRef = useRef<Record<string | number, { count: number; timeoutId: ReturnType<typeof setTimeout> }>>({});

  type OssUser = { name: string; gym: string; created_at: string };
  const [ossModal, setOssModal] = useState<{ tournamentId: string | number; users: OssUser[] } | null>(null);
  const [ossModalLoading, setOssModalLoading] = useState(false);

  const handleOssCountClick = useCallback(async (e: React.MouseEvent, tournamentId: string | number) => {
    e.stopPropagation();
    e.preventDefault();
    setOssModalLoading(true);
    setOssModal({ tournamentId, users: [] });
    try {
      const { data } = await api.get(`/tournaments/${tournamentId}/oss/users`);
      setOssModal({ tournamentId, users: data.users });
      // Atualiza o total com o valor real do servidor
      const total = data.users.reduce((sum: number, u: any) => sum + (u.totalOss || 1), 0);
      setOssMap(prev => ({ ...prev, [tournamentId]: total }));
    } finally {
      setOssModalLoading(false);
    }
  }, []);

  const handleOss = useCallback((e: React.MouseEvent, tournamentId: string | number) => {
    e.stopPropagation();
    e.preventDefault();

    const token = window.localStorage.getItem("@torneiofacil/token");
    if (!token && !allowAnonymousOssRef.current) {
      navigate("/login");
      return;
    }

    const btn = e.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top;

    // Acumula cliques localmente
    const prev = ossConsecutiveRef.current[tournamentId];
    if (prev) clearTimeout(prev.timeoutId);

    const consecutiveCount = (prev?.count ?? 0) + 1;

    // Incrementa contador na tela imediatamente
    setOssMap(prevMap => ({ ...prevMap, [tournamentId]: (prevMap[tournamentId] || 0) + 1 }));

    // Barra com progressão logarítmica: cresce rápido no início, fica lento depois
    // 500 cliques = 50%, 2000 cliques = 100%, com força puxando pra trás
    const currentBar = ossCombo[tournamentId] ?? 0;
    // Quanto mais cheia, menor o ganho por clique
    const resistance = 1 - (currentBar / 100);
    const push = resistance * 2.5;
    const newBar = Math.min(currentBar + push, 100);
    setOssCombo(prev => ({ ...prev, [tournamentId]: newBar }));

    // Envia 1 único request 500ms após o último clique
    const resetTimeout = setTimeout(() => {
      const totalClicks = ossConsecutiveRef.current[tournamentId]?.count ?? 1;
      delete ossConsecutiveRef.current[tournamentId];

      // Explosão + resultado flutuante
      setOssExplosion(prev => ({ ...prev, [tournamentId]: true }));
      setOssCombo(prev => { const n = { ...prev }; delete n[tournamentId]; return n; });
      setOssComboResult(prev => ({
        ...prev,
        [tournamentId]: {
          count: totalClicks,
          x: 20 + Math.random() * 60,
          y: 20 + Math.random() * 40,
        },
      }));
      setTimeout(() => {
        setOssExplosion(prev => { const n = { ...prev }; delete n[tournamentId]; return n; });
        setOssComboResult(prev => { const n = { ...prev }; delete n[tournamentId]; return n; });
      }, 1500);

      const headers: any = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      api.post(`/tournaments/${tournamentId}/oss`, { count: totalClicks }, { headers }).then(({ data }) => {
        setOssMap(prevMap => ({ ...prevMap, [tournamentId]: data.totalCount }));
      }).catch((err) => {
        if (err?.response?.status === 403) {
          // Backend rejeitou: reverter contagem local e ir pro login
          setOssMap(prevMap => ({
            ...prevMap,
            [tournamentId]: Math.max((prevMap[tournamentId] || 0) - totalClicks, 0),
          }));
          allowAnonymousOssRef.current = false;
          setAllowAnonymousOss(false);
          navigate("/login");
        }
      });
    }, 1000);

    ossConsecutiveRef.current[tournamentId] = { count: consecutiveCount, timeoutId: resetTimeout };

    // 1 partícula por clique
    const newParticle: OssParticleData = {
      id: ++ossParticleIdRef.current,
      x: cx + (Math.random() - 0.5) * 50,
      y: cy,
      delay: 0,
      color: (Math.random() > 0.5 ? "red" : "blue") as "red" | "blue",
    };

    setOssParticles(p => [...p, newParticle]);

    const particleId = newParticle.id;
    setTimeout(() => {
      setOssParticles(p => p.filter(particle => particle.id !== particleId));
    }, 1500);
  }, [navigate, ossCombo, allowAnonymousOss]);

  // Força que puxa a barra pra trás constantemente
  useEffect(() => {
    const activeIds = Object.keys(ossCombo);
    if (activeIds.length === 0) return;

    const interval = setInterval(() => {
      setOssCombo(prev => {
        const next = { ...prev };
        for (const id of Object.keys(next)) {
          // Puxa mais forte quanto mais cheia a barra
          const decay = 0.2 + (next[id] / 100) * 0.5;
          next[id] = Math.max(next[id] - decay, 0);
          if (next[id] === 0) delete next[id];
        }
        return next;
      });
    }, 50);

    return () => clearInterval(interval);
  }, [Object.keys(ossCombo).length]);

  // Carrega contagem de OSS e config ao montar
  useEffect(() => {
    api.get('/tournaments/oss/counts')
      .then(({ data }) => {
        const counts: Record<string | number, number> = {};
        data.counts?.forEach((c: { tournamentId: number; count: number }) => {
          counts[c.tournamentId] = c.count;
        });
        setOssMap(prev => ({ ...prev, ...counts }));
      })
      .catch(() => {});

    api.get('/tournaments/oss/config')
      .then(({ data }) => {
        setAllowAnonymousOss(data.allowAnonymous);
        allowAnonymousOssRef.current = data.allowAnonymous;
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!showAd) return;
    const interval = setInterval(() => {
      setAdIndex((prev) => (prev + 1) % adOrder.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [showAd]);

  const isMobile = useMediaQuery("(max-width:600px)");
  const instagramTekton = "https://www.tekton3d.com.br/";

  const openTournaments = tournamentsData.filter((t: any) => !t.isClosed).length;
  const closedTournaments = tournamentsData.filter((t: any) => t.isClosed).length;

  /* ── Filtro ── */
  const filteredTournaments = useMemo(() => {
    let result = [...tournamentsData];

    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      result = result.filter((t: any) => t.title.toLowerCase().includes(term));
    }

    switch (activeFilter) {
      case "open":
        result = result.filter((t: any) => !t.isClosed);
        break;
      case "closed":
        result = result.filter((t: any) => t.isClosed);
        break;
      case "free":
        result = result.filter((t: any) => FREE_REGEX.test(t.description));
        break;
      case "limited":
        result = result.filter((t: any) => t.amountRegistrationPaid && !t.isClosed);
        break;
    }

    return result;
  }, [activeFilter, searchTerm]);

  const displayedTournaments = filteredTournaments.slice(0, displayCount);
  const hasMore = displayCount < filteredTournaments.length;

  const handleFilterChange = (filter: FilterType) => {
    setActiveFilter(filter);
    setDisplayCount(ITEMS_PER_BATCH);
  };

  /* ── Infinite scroll ── */
  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    setLoading(true);
    setTimeout(() => {
      setDisplayCount((prev) => prev + ITEMS_PER_BATCH);
      setLoading(false);
    }, 400);
  }, [loading, hasMore]);

  useEffect(() => {
    const handleScroll = () => {
      if (!hasMore || loading) return;
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      if (scrollTop + windowHeight >= docHeight - 200) {
        loadMore();
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loadMore, hasMore, loading]);

  useEffect(() => {
    if (selectedTournament) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [selectedTournament]);

  return (
    <div translate="no">
      <NavBar />

      {/* ── OSS Particles ── */}
      {ossParticles.map(p => (
        <OssParticle
          key={p.id}
          $x={p.x}
          $y={p.y}
          $delay={p.delay}
          $color={p.color}
        >
          oss
        </OssParticle>
      ))}

      {/* ── Hero ── */}
      <HeroSection>
        <HeroContent>
          <HeroEyebrow>Plataforma de Torneios</HeroEyebrow>
          <HeroTitle>
            Encontre e inscreva-se em<br />
            <span>torneios</span> de Jiu Jitsu
          </HeroTitle>
          <HeroSubtitle>
            Crie, organize e gerencie seus torneios. Plataforma completa para competições de artes marciais.
          </HeroSubtitle>
          <StatsRow>
            <StatItem>
              <StatNumber>{tournamentsData.length}</StatNumber>
              <StatLabel>torneios</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>{openTournaments}</StatNumber>
              <StatLabel>abertos</StatLabel>
            </StatItem>
            <StatItem>
              <StatNumber>{closedTournaments}</StatNumber>
              <StatLabel>encerrados</StatLabel>
            </StatItem>
          </StatsRow>
        </HeroContent>
      </HeroSection>

      {/* ── Controls ── */}
      <ControlsSection>
        <SearchBar>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <SearchInput
            type="text"
            placeholder="Buscar torneios por nome..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setDisplayCount(ITEMS_PER_BATCH);
            }}
          />
        </SearchBar>

        <FilterRow>
          <FilterLabel>Status</FilterLabel>
          {FILTERS.map((f) => (
            <TfChip
              key={f.key}
              active={activeFilter === f.key}
              onClick={() => handleFilterChange(f.key)}
            >
              {f.label}
            </TfChip>
          ))}
        </FilterRow>

        <ViewControls>
          <ResultsCount>
            <span>Exibindo <strong>{filteredTournaments.length}</strong>{` torneio${filteredTournaments.length !== 1 ? "s" : ""}`}</span>
          </ResultsCount>
        </ViewControls>
      </ControlsSection>

      {/* ── Grid ── */}
      <TournamentGridContainer>
        <TournamentGrid>
          {displayedTournaments.length === 0 && !loading && (
            <EmptyState>
              <h3>Nenhum torneio encontrado</h3>
              <p>Tente alterar os filtros ou o termo de busca.</p>
            </EmptyState>
          )}

          {displayedTournaments.map((t: any, i: number) => {
            const badgeStatus = getBadgeStatus(t);
            const isFree = FREE_REGEX.test(t.description);
            const tiers = extractPriceTiers(t.description);
            const ossCount = ossMap[t.id] || 0;

            return (
              <TournamentCard
                key={t.id}
                delay={Math.min(i % ITEMS_PER_BATCH, 5) * 50}
              >
                {/* Top Bar */}
                <CardTopBar>
                  {ossCombo[t.id] !== undefined && (
                    <OssComboBar style={{ width: `${ossCombo[t.id]}%` }} />
                  )}
                  {ossExplosion[t.id] && <OssExplosion />}
                  <CardBadges>
                    <TfBadge status="sport">{t.sport || "Jiu-Jitsu"}</TfBadge>
                    <TfBadge status={badgeStatus}>{getBadgeLabel(badgeStatus)}</TfBadge>
                    {t.isCasadas && <TfBadge status="casadas">Lutas Casadas</TfBadge>}
                  </CardBadges>
                  <CardTopBarActions>
                    <OssBubble
                      onClick={(e: React.MouseEvent) => handleOss(e, t.id)}
                      title="OSS!"
                    >
                      <OssLabel>👊 oss</OssLabel>
                    </OssBubble>
                    {ossCount > 0 && (
                      <OssCount
                        onClick={(e: React.MouseEvent) => handleOssCountClick(e, t.id)}
                        style={{ cursor: "pointer" }}
                      >
                        +{formatCount(ossCount)}
                      </OssCount>
                    )}
                    <ShareBubble
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        const text = buildShareText(t);
                        const url = `${window.location.origin}/tournament/${t.link}`;
                        if (navigator.share) {
                          navigator.share({ title: t.title, text });
                        } else {
                          navigator.clipboard.writeText(text);
                        }
                      }}
                    >
                      <FiShare2 />
                    </ShareBubble>
                  </CardTopBarActions>
                </CardTopBar>

                {/* Banner */}
                <CardBanner
                  onClick={() => setSelectedTournament(t)}
                  style={{ cursor: "pointer" }}
                >
                  <img src={t.image} alt={t.title} loading="lazy" />
                  <CardBannerOverlay />
                  {ossComboResult[t.id] && (
                    <OssComboFloat $x={ossComboResult[t.id].x} $y={ossComboResult[t.id].y}>
                      +{ossComboResult[t.id].count} 👊
                    </OssComboFloat>
                  )}
                </CardBanner>

                {/* Body */}
                <CardBody>
                  <CardTitle>{t.title.toUpperCase()}</CardTitle>

                  {/* Warning for closed */}
                  {t.isClosed && (
                    <CardWarning>
                      ⚠️ Inscrições encerradas
                    </CardWarning>
                  )}

                  {/* Capacity bar + Footer agrupados no final */}
                  <CardBottom>
                    {t.amountRegistrationPaid && (
                      <CapacitySection>
                        <TournamentProgress
                          title={t.title}
                          maxInscricoes={t.maxInscricoes}
                          ajusteQuantidadeDeInscricoes={t.ajusteQuantidadeDeInscricoes}
                          IsQuantidadeInscricaoEstatica={t.IsQuantidadeInscricaoEstatica}
                          quantidadeInscricaoEstatica={t.quantidadeInscricaoEstatica}
                          variant="capacity"
                        />
                      </CapacitySection>
                    )}

                    <CardFooter>
                      <TfBtn
                        to={`/tournament/${t.link}`}
                        variant={isFree ? "free" : t.isClosed ? "navy" : "primary"}
                        disabled={t.isClosed}
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); window.scrollTo(0, 0); }}
                      >
                        {isFree ? "🆓 Gratuito" : t.isClosed ? "Encerrado" : "Inscrever-se"}
                      </TfBtn>
                      <TfBtn
                        to="#"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setSelectedTournament(t);
                        }}
                        variant="secondary"
                      >
                        Detalhes
                      </TfBtn>
                    </CardFooter>
                  </CardBottom>
                </CardBody>
              </TournamentCard>
            );
          })}

          {loading && <LoadingIndicator>Carregando torneios...</LoadingIndicator>}
        </TournamentGrid>
      </TournamentGridContainer>

      {/* ── Detail Panel ── */}
      {selectedTournament && (() => {
        const st = selectedTournament;
        const stTiers = extractPriceTiers(st.description);
        const stFree = FREE_REGEX.test(st.description);
        const stBadgeStatus = getBadgeStatus(st);

        return (
          <>
            <DetailOverlay onClick={() => setSelectedTournament(null)} />
            <DetailPanel>
              <DetailHero>
                <img src={st.image} alt={st.title} />
                <DetailHeroOverlay />
                <DetailBack onClick={() => setSelectedTournament(null)}>
                  &#8592;
                </DetailBack>
              </DetailHero>
              <DetailBody>
                <DetailBadges>
                  <TfBadge status="sport">{st.sport || "Jiu-Jitsu"}</TfBadge>
                  <TfBadge status={stBadgeStatus}>{getBadgeLabel(stBadgeStatus)}</TfBadge>
                  {st.isCasadas && <TfBadge status="casadas">Lutas Casadas</TfBadge>}
                </DetailBadges>
                <DetailTitle>{st.title.toUpperCase()}</DetailTitle>

                {/* Lotes completos */}
                {stTiers.length > 0 && (
                  <DetailSection>
                    <DetailSectionTitle>Lotes</DetailSectionTitle>
                    {stTiers.map((tier, idx) => (
                      <PriceSection key={idx} style={idx > 0 ? { marginTop: 8 } : undefined}>
                        <PriceTier>
                          <PriceTierName>
                            <PriceTierDot variant={tier.isFirst ? "active" : "neutral"} />
                            <strong>{tier.name}</strong>
                          </PriceTierName>
                          {tier.deadline && (
                            <PriceTierDeadline>{tier.deadline}</PriceTierDeadline>
                          )}
                        </PriceTier>
                        {tier.prices.map((p, pIdx) => (
                          <PriceTier key={pIdx}>
                            <PriceTierName style={{ paddingLeft: 12 }}>
                              {p.category || "Inscrição"}
                            </PriceTierName>
                            <PriceTierRight>
                              <PriceTierValue promo={tier.isFirst} free={stFree}>
                                {p.price}
                              </PriceTierValue>
                            </PriceTierRight>
                          </PriceTier>
                        ))}
                      </PriceSection>
                    ))}
                  </DetailSection>
                )}

                {/* Descrição / Informações */}
                {st.description && (
                  <DetailSection>
                    <DetailSectionTitle>Informações</DetailSectionTitle>
                    <DetailDescription
                      dangerouslySetInnerHTML={{ __html: st.description }}
                    />
                  </DetailSection>
                )}

                {/* Capacidade */}
                {st.amountRegistrationPaid && (
                  <DetailCapacity>
                    <TournamentProgress
                      title={st.title}
                      maxInscricoes={st.maxInscricoes}
                      ajusteQuantidadeDeInscricoes={st.ajusteQuantidadeDeInscricoes}
                      IsQuantidadeInscricaoEstatica={st.IsQuantidadeInscricaoEstatica}
                      quantidadeInscricaoEstatica={st.quantidadeInscricaoEstatica}
                      variant="capacity"
                    />
                  </DetailCapacity>
                )}

                {st.isClosed && (
                  <CardWarning>
                    ⚠️ Inscrições encerradas
                  </CardWarning>
                )}
              </DetailBody>
              <DetailActions>
                <TfBtn
                  to={`/tournament/${st.link}`}
                  variant={stFree ? "free" : st.isClosed ? "navy" : "primary"}
                  disabled={st.isClosed}
                  onClick={() => window.scrollTo(0, 0)}
                >
                  {stFree ? "🆓 Gratuito" : st.isClosed ? "Encerrado" : "Inscrever-se Agora"}
                </TfBtn>
                <TfBtn
                  to="#"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    setSelectedTournament(null);
                  }}
                  variant="secondary"
                >
                  Fechar
                </TfBtn>
              </DetailActions>
            </DetailPanel>
          </>
        );
      })()}

      {/* ── Ad Card (alterna a cada 5s) ── */}
      {showAd && currentAd === "intheguard" && (
        <AdCard key="ad-intheguard" style={{ background: "#fff" }}>
          <div style={{ position: "relative" }}>
            <AdClose onClick={() => setShowAd(false)}>✕</AdClose>
            <a
              href="https://www.intheguard.com.br/?utm_source=torneiofacil&utm_medium=site&utm_campaign=home"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/ads/intheguard-logo.jpg"
                alt="In The Guard - Brazilian Fightwear"
                style={{ width: "100%", height: "auto", display: "block" }}
              />
            </a>
          </div>
          <AdBody>
            <p>Kimonos e equipamentos de luta</p>
            <small>@intheguardfightwear</small>
            <br />
            <a
              href="https://www.intheguard.com.br/?utm_source=torneiofacil&utm_medium=site&utm_campaign=home"
              target="_blank"
              rel="noopener noreferrer"
            >
              Visite a loja
            </a>
          </AdBody>
          <AdProgressBar key={`progress-${currentAd}`} />
        </AdCard>
      )}

      {showAd && currentAd === "tekton" && (
        <AdCard key="ad-tekton">
          <div style={{ position: "relative" }}>
            <AdClose onClick={() => setShowAd(false)}>✕</AdClose>
            <img
              src="/ads/LOGO_TEKTON3D.jpg"
              alt="logo tekton"
              style={{ width: "100%", height: "auto", display: "block" }}
            />
          </div>
          <AdBody>
            <p>Confecção de troféus, medalhas e brindes em 3D.</p>
            <small>@tekton.3dfarm</small>
            <br />
            <a href={instagramTekton} target="_blank" rel="noopener noreferrer">
              Clique aqui
            </a>
          </AdBody>
          <AdProgressBar key={`progress-${currentAd}`} />
        </AdCard>
      )}

      {showAd && currentAd === "redlions" && (
        <AdCard key="ad-redlions" style={{ background: "#fff" }}>
          <div style={{ position: "relative" }}>
            <AdClose onClick={() => setShowAd(false)}>✕</AdClose>
            <a
              href="https://www.instagram.com/red_lions_international_matriz/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/ads/RedLions.png"
                alt="Red Lions International - BJJ & MMA"
                style={{ width: "100%", height: "auto", maxHeight: 240, objectFit: "contain", display: "block" }}
              />
            </a>
          </div>
          <AdBody>
            <p>Brazilian Jiu Jitsu & MMA</p>
            <small>@red_lions_international_matriz</small>
            <br />
            <a
              href="https://www.instagram.com/red_lions_international_matriz/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Siga no Instagram
            </a>
          </AdBody>
          <AdProgressBar key={`progress-${currentAd}`} />
        </AdCard>
      )}

      {showAd && currentAd === "akto" && (
        <AdCard key="ad-akto">
          <div style={{ position: "relative" }}>
            <AdClose onClick={() => setShowAd(false)}>✕</AdClose>
            <a
              href="https://akto.com.br/?utm_source=torneiofacil&utm_medium=social&utm_content=link_in_torneiofacil"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="/ads/akto-logo.png"
                alt="Akto - Internet dedicada para empresas"
                style={{ width: "100%", height: "auto", display: "block" }}
              />
            </a>
          </div>
          <AdBody>
            <p>Internet dedicada para empresas</p>
            <a
              href="https://akto.com.br/?utm_source=torneiofacil&utm_medium=social&utm_content=link_in_torneiofacil"
              target="_blank"
              rel="noopener noreferrer"
            >
              Saiba mais
            </a>
          </AdBody>
          <AdProgressBar key={`progress-${currentAd}`} />
        </AdCard>
      )}

      {/* ── Banner Dialog ── */}
      <BannerDialog
        open={showBanner}
        onClose={() => setShowBanner(false)}
        isMobile={isMobile}
      />

      {/* ── OSS Users Modal ── */}
      {ossModal && (
        <OssModalOverlay onClick={() => setOssModal(null)}>
          <OssModalBox onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <OssModalHeader>
              <h3>👊 Quem deu OSS</h3>
              <button onClick={() => setOssModal(null)}>✕</button>
            </OssModalHeader>
            {ossModalLoading ? (
              <p style={{ textAlign: "center", color: "#888", margin: 0 }}>Carregando...</p>
            ) : ossModal.users.length === 0 ? (
              <p style={{ textAlign: "center", color: "#888", margin: 0 }}>Nenhum OSS ainda.</p>
            ) : (
              <OssModalList>
                {ossModal.users.map((u: any, i: number) => (
                  <OssModalItem key={i}>
                    <span style={{ fontWeight: 700, color: "#1A2252", minWidth: "20px" }}>{i + 1}º</span>
                    <OssModalAvatar style={
                      u.avatar
                        ? { background: `url(${(api.defaults.baseURL?.replace(/\/$/, '') || '')}${u.avatar}) center/cover`, border: "2px solid #d1d5db" }
                        : u.name === "Anônimo" ? { background: "#9ca3af" } : {}
                    }>
                      {!u.avatar && (u.name === "Anônimo" ? "👤" : u.name.charAt(0).toUpperCase())}
                    </OssModalAvatar>
                    <OssModalInfo>
                      <span style={{ textTransform: "uppercase" }}>
                        {u.name === "Anônimo" ? <>Anônimos <span style={{ fontWeight: 400, fontSize: "0.7rem" }}>(não logados)</span></> : u.name}
                      </span>
                      {u.name !== "Anônimo" && (
                        <small style={{ textTransform: "uppercase" }}>{u.gym || "Academia não informada"}</small>
                      )}
                    </OssModalInfo>
                    <span style={{ color: "#1A2252", fontSize: "0.75rem", fontWeight: 700, flexShrink: 0 }}>
                      {u.totalOss || 1}x 👊
                    </span>
                  </OssModalItem>
                ))}
              </OssModalList>
            )}
          </OssModalBox>
        </OssModalOverlay>
      )}
    </div>
  );
};

export default Home;
